import React from 'react';
import { View, TouchableOpacity, StyleSheet, ActivityIndicator } from 'react-native';
import { Text, TextInput } from '@/components/common/text';
import { SVG_COMPONENTS as ADMIN_SVG_COMPONENTS } from '@/assets/admin';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { REDESIGN_TYPOGRAPHY, REDESIGN_SPACING } from '@/constants/residents/homeConstants';
import { RESIDENTS_MOBILE_MESSAGE_MAX_LENGTH } from '@/constants/residents/mobile/message';

type MessageInputBarProps = {
  messageText: string;
  setMessageText: (text: string) => void;
  onSend: () => void;
  disabled?: boolean;
  placeholder?: string;
  onPressPaperclip?: () => void;
  onFocusScrollToBottom?: () => void;
  maxLength?: number;
  isSending?: boolean;
};

export const MessageInputBar: React.FC<MessageInputBarProps> = ({
  messageText,
  setMessageText,
  onSend,
  disabled,
  placeholder = 'Your message...',
  onPressPaperclip,
  onFocusScrollToBottom,
  maxLength = RESIDENTS_MOBILE_MESSAGE_MAX_LENGTH,
  isSending = false,
}) => {
  const { colors } = useThemeColors();

  const remaining = maxLength - messageText.length;
  const overLimit = messageText.length > maxLength;
  const atOrOverLimit = remaining <= 0;
  const finalDisabled = !!disabled || overLimit || isSending;

  return (
    <View style={[stylesRef.messageInputFooter, { borderTopColor: colors.separator }]}>
      <View style={stylesRef.inputRowContainer}>
        <View style={stylesRef.inputRow}>
          <View style={stylesRef.attachButtonWrapper}>
            <TouchableOpacity style={stylesRef.attachButton} onPress={onPressPaperclip}>
              <ADMIN_SVG_COMPONENTS.PAPERCLIP width={20} height={20} color={colors.text.subtle} />
            </TouchableOpacity>
          </View>

          <TextInput
            style={[stylesRef.messageInput, { color: colors.text.primary }]}
            value={messageText}
            onChangeText={setMessageText}
            placeholder={placeholder}
            placeholderTextColor={colors.text.subtle}
            multiline
            textAlignVertical="top"
            onFocus={onFocusScrollToBottom}
          />

          {atOrOverLimit ? (
            <View style={stylesRef.counterContainer}>
              <Text scale="micro" style={stylesRef.counterText}>{remaining}</Text>
            </View>
          ) : (
            <TouchableOpacity
              onPress={onSend}
              disabled={finalDisabled}
              style={stylesRef.sendButton}
              accessibilityLabel="Send Message"
            >
              {isSending ? (
                <ActivityIndicator size="small" color={colors.text.primary} />
              ) : !finalDisabled ? (
                <RESIDENTS_SVG_COMPONENTS.CHAT_SEND_ACTIVE_BUTTON width={34} height={34} />
              ) : (
                <RESIDENTS_SVG_COMPONENTS.CHAT_SEND_INACTIVE_BUTTON width={34} height={34} />
              )}
            </TouchableOpacity>
          )}
        </View>
      </View>
    </View>
  );
};

const stylesRef = StyleSheet.create({
  messageInputFooter: {
    borderTopWidth: 1,
  },
  inputRowContainer: {
    paddingHorizontal: REDESIGN_SPACING.horizontalPadding,
    paddingVertical: 20,
  },
  inputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  attachButtonWrapper: {
    paddingTop: 5,
  },
  attachButton: {
    width: 24,
    height: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  messageInput: {
    flex: 1,
    minHeight: 21,
    maxHeight: 100,
    ...REDESIGN_TYPOGRAPHY.body,
    paddingVertical: 6,
  },
  sendButton: {
    width: 34,
    height: 34,
    alignItems: 'center',
    justifyContent: 'center',
  },
  counterContainer: {
    width: 34,
    height: 34,
    alignItems: 'center',
    justifyContent: 'center',
  },
  counterText: {
    color: PRIMITIVE_COLORS.resioPink,
    ...REDESIGN_TYPOGRAPHY.small,
  },
});

export default MessageInputBar;
